import { Text } from 'foundations-components/components/Text';
import { Button } from 'foundations-components/transitional/components/Button';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { PopoverBody, PopoverFooter, PopoverHeader } from 'foundations-components/transitional/components/Popover';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { useCallback, useState } from 'react';
import ReferenceLiteSearchSelect from 'reference-resolvers-lite/components/ReferenceLiteSearchSelect';
import { PIPELINE_WITH_PERMISSIONS } from 'reference-resolvers-lite/constants/ReferenceTypes';
import { createStandardCachedResolver } from 'reference-resolvers-lite/utils/createStandardCachedResolver';
import { useHttpClient } from '../../../client/HttpClientContext';
import { createCachedCustomResolver } from '../../../resolvers/createCachedCustomResolver';
import { PIPELINE_STAGE_IN_PIPELINE } from '../../../resolvers/CustomResolverReferenceTypes';
import { referenceToOptionWithReferenceV2 } from '../../../utils/referenceLiteSearchSelectUtils';
import { RESOLVER_DEBOUNCE_KEY, TIME_TOKENS } from '../../../utils/timeTokens';
import { useObjectId } from '../../../v2/hooks/useObjectId';
import { useObjectTypeId } from '../../../v2/hooks/useObjectTypeId';
import { isBETPortal } from '../../../v2/utils/isBETPortal';
import { useGetLoadStageReferences } from '../hooks/useGetLoadStageReferences';
import { useRecordInitialPipelineAndStage } from '../hooks/useRecordInitialPipelineAndStage';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const PipelinePopoverBody = props => {
  const {
    onConfirm,
    onCancel,
    defaultPipelineId,
    defaultPipelineStageId,
    inputMode,
    stageChangeValidatorLoading,
    showAutoSaveText = false
  } = props;
  const objectTypeId = useObjectTypeId();
  const objectId = useObjectId();
  const httpClient = useHttpClient();
  const isPortal53 = isBETPortal();
  const [pipelineId, setPipelineId] = useState(defaultPipelineId);
  const [pipelineStageId, setPipelineStageId] = useState(defaultPipelineStageId);
  const [stageReference, setStageReference] = useState(null);
  const {
    recordPipelineId,
    recordStageId,
    propertyValuesLoading
  } = useRecordInitialPipelineAndStage({
    httpClient,
    objectTypeId,
    inputMode,
    objectId
  });
  const loadStageReferences = useGetLoadStageReferences({
    httpClient,
    objectTypeId,
    pipelineId,
    inputMode,
    objectId
  });

  // NOTE: we cannot use `usePropertyComponentResolver` here because we need
  // `PIPELINE_WITH_PERMISSIONS` to ensure that PLP-hidden pipelines are disabled.
  // Making `PIPELINE_WITH_PERMISSIONS` the default for pipeline properties in
  // `getReferenceTypeFromProperty` (instead of `PIPELINE`) would break
  // filters/workflows by preventing users from selecting these pipelines
  const pipelineResolver = createStandardCachedResolver(PIPELINE_WITH_PERMISSIONS, objectTypeId, {
    httpClient,
    debouncePeriod: TIME_TOKENS[RESOLVER_DEBOUNCE_KEY]
  });

  // parameterize the resolver with `pipelineId` so it only shows stages in the selected pipeline
  const pipelineStageResolver = createCachedCustomResolver(PIPELINE_STAGE_IN_PIPELINE, objectTypeId, {
    pipelineId: pipelineId === null ? undefined : pipelineId,
    httpClient
  });

  // Use `onSelectedOptionChange` to handle changes instead of `onChange`,
  // because we need access to the selected `Reference` to read its
  // pipelineId (changing stage should also change pipeline).
  const handlePipelineChange = useCallback(e => {
    const reference = e.target.value.reference;
    const newPipelineId = `${e.target.value.value}`;
    const defaultStageId = reference && reference.additionalProperties && reference.additionalProperties.defaultPipelineStageId ? `${reference.additionalProperties.defaultPipelineStageId}` : null;
    const newStageId = !propertyValuesLoading && newPipelineId === recordPipelineId && recordStageId ? recordStageId : defaultStageId;
    setPipelineId(newPipelineId);
    setPipelineStageId(isPortal53 ? null : newStageId);
  }, [recordPipelineId, recordStageId, propertyValuesLoading, isPortal53]);
  const handlePipelineStageChange = useCallback(e => {
    const reference = e.target.value.reference;
    const newStageId = `${e.target.value.value}`;
    const newPipelineId = reference && reference.additionalProperties && reference.additionalProperties.pipelineId ? `${reference.additionalProperties.pipelineId}` : null;
    setPipelineId(newPipelineId);
    setPipelineStageId(newStageId);
    setStageReference(reference);
  }, []);
  const handleConfirm = useCallback(() => {
    onConfirm({
      pipelineId,
      pipelineStageId,
      stageReference: stageReference !== null && stageReference !== void 0 ? stageReference : undefined
    });
  }, [onConfirm, pipelineId, pipelineStageId, stageReference]);
  const disabled = !(pipelineId && pipelineStageId);
  const confirmLabel = showAutoSaveText ? /*#__PURE__*/_jsx(FormattedMessage, {
    message: "customerDataProperties.PropertyInputPipelineV2.save"
  }) : /*#__PURE__*/_jsx(FormattedMessage, {
    message: "customerDataProperties.PropertyInputPipelineV2.apply"
  });
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(PopoverHeader, {
      showCloseButton: true,
      onOpenChange: e => {
        if (e.target.value === false) {
          onCancel();
        }
      },
      children: /*#__PURE__*/_jsx(Text, {
        as: "h5",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "customerDataProperties.PropertyInputPipelineV2.header"
        })
      })
    }), /*#__PURE__*/_jsxs(PopoverBody, {
      paddingBlockStart: "300",
      children: [propertyValuesLoading && /*#__PURE__*/_jsx(LoadingSpinner, {
        layout: "centered"
      }), !propertyValuesLoading && /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(FormControl, {
          label: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "customerDataProperties.PropertyInputPipelineV2.pipeline"
          }),
          children: /*#__PURE__*/_jsx(ReferenceLiteSearchSelect, {
            referenceToOption: referenceToOptionWithReferenceV2,
            onSelectedOptionChange: handlePipelineChange,
            resolver: pipelineResolver,
            value: pipelineId
          })
        }), /*#__PURE__*/_jsx(FormControl, {
          label: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "customerDataProperties.PropertyInputPipelineV2.stage"
          }),
          children: /*#__PURE__*/_jsx(ReferenceLiteSearchSelect, {
            referenceToOption: referenceToOptionWithReferenceV2,
            onSelectedOptionChange: handlePipelineStageChange,
            resolver: pipelineStageResolver,
            value: pipelineStageId,
            loadReferences: loadStageReferences
            // The reason we don't need to add cache={false} here is because the record can't get saved while the modal is visible
            // For a smoother experience, we can utilize UISelect's option cache since we don't need to refetch validation when the input opens
          })
        })]
      })]
    }), /*#__PURE__*/_jsxs(PopoverFooter, {
      children: [/*#__PURE__*/_jsx(Tooltip, {
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "customerDataProperties.PropertyInputPipelineV2.confirmDisabledTooltip"
        }),
        disabled: !disabled,
        children: /*#__PURE__*/_jsx(Button, {
          disabled: disabled || stageChangeValidatorLoading,
          onClick: handleConfirm,
          size: "extra-small",
          use: "primary",
          children: confirmLabel
        })
      }), /*#__PURE__*/_jsx(Button, {
        onClick: onCancel,
        size: "extra-small",
        use: "tertiary-light",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "customerDataProperties.PropertyInputPipelineV2.cancel"
        })
      })]
    })]
  });
};
export default PipelinePopoverBody;